import React from 'react';
import BEM from 'common/bem';

const classes = BEM.with('CursorPagination');

export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

type CursorPaginationProps = {
  /** 1-based page currently shown. */
  currentPage: number;
  /**
   * How many pages have been visited so far. Cursor-paginated endpoints have no true total, so
   * numbered buttons are only rendered for pages we already hold a token for.
   */
  visitedPageCount: number;
  /** Whether the current page's response carried a nextToken. */
  hasNextPage: boolean;
  /** Rows on the current page. */
  rowCount: number;
  pageSize: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  disabled?: boolean;
  testIdPrefix?: string;
};

/**
 * "Showing 1–25 of 1,000+" when more pages exist (no true total is available), or an exact
 * "of {end}" once the last page has been reached.
 */
export function formatCursorRangeLabel(
  currentPage: number,
  pageSize: number,
  rowCount: number,
  hasNextPage: boolean
): string {
  if (rowCount === 0) return 'Showing 0 results';
  const start = (currentPage - 1) * pageSize + 1;
  const end = start + rowCount - 1;
  const total = hasNextPage ? `${end.toLocaleString()}+` : end.toLocaleString();
  return `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${total}`;
}

/**
 * Prev/Next pagination for cursor (nextToken) based endpoints, per the archiving UX spec §8.10.
 * Unlike admin/shared-components/Pagination, it doesn't need a total page count.
 */
export function CursorPagination({
  currentPage,
  visitedPageCount,
  hasNextPage,
  rowCount,
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageChange,
  onPageSizeChange,
  disabled = false,
  testIdPrefix = 'cursor-pagination',
}: CursorPaginationProps) {
  const pageNumbers = Array.from(
    { length: Math.max(visitedPageCount, currentPage) },
    (_, i) => i + 1
  );

  return (
    <nav aria-label="Pagination" className={classes()} data-test-id={testIdPrefix}>
      <div className={classes('pages')}>
        <button
          aria-label="Previous page"
          className={classes('arrow-button')}
          data-test-id={`${testIdPrefix}-prev`}
          disabled={disabled || currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
          type="button"
        >
          ←
        </button>
        {pageNumbers.map((page) => (
          <button
            aria-current={page === currentPage ? 'page' : undefined}
            className={classes('page-button', { active: page === currentPage })}
            data-test-id={`${testIdPrefix}-page-${page}`}
            disabled={disabled && page !== currentPage}
            key={page}
            onClick={() => {
              if (page !== currentPage) onPageChange(page);
            }}
            type="button"
          >
            {page}
          </button>
        ))}
        <button
          aria-label="Next page"
          className={classes('arrow-button')}
          data-test-id={`${testIdPrefix}-next`}
          disabled={disabled || !hasNextPage}
          onClick={() => onPageChange(currentPage + 1)}
          type="button"
        >
          →
        </button>
      </div>
      <span className={classes('range')} data-test-id={`${testIdPrefix}-range`}>
        {formatCursorRangeLabel(currentPage, pageSize, rowCount, hasNextPage)}
      </span>
      <label className={classes('page-size')}>
        <span>Rows:</span>
        <select
          className={classes('page-size-select')}
          data-test-id={`${testIdPrefix}-page-size`}
          disabled={disabled}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
          value={pageSize}
        >
          {pageSizeOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>
    </nav>
  );
}

export default CursorPagination;
